<template>
    <div>
        <div
            class="stone-recommendations"
            data-qa="stone-recommendations"
        >
            <div class="consultant-message">
                <AvatarStack />

                <I18n
                    keypath="stone_recommendations.intro"
                    tag="p"
                    class="instruction-text"
                >
                    <template #clickHere>
                        <button
                            class="cta"
                            @click="openSpeakToExpertModal"
                        >
                            {{ t('stone_recommendations.click_here') }}
                        </button>
                    </template>
                </I18n>
            </div>

            <Tabs
                :current-tab-resolver
                :tabs
            />

            <div
                v-if="
                    stoneRecommendations?.__typename ===
                    'StoneRecommendationsResponseSuccess'
                "
                class="recommendations-wrapper"
            >
                <div
                    v-qa="'budget-slider'"
                    class="budget-slider"
                >
                    <span class="label">
                        {{ t('stone_recommendations.total_budget') }}

                        <LazyTooltip
                            inline
                            path="/stone-recommendations/total-budget"
                        />
                    </span>

                    <div
                        v-if="boundsInCurrency"
                        class="range-slider"
                    >
                        <InputRangeTwoKnobbedExponentialOkWithInputs
                            v-if="budgetFilterForUi"
                            v-model="budgetFilterForUi"
                            :bounds="{
                                min: parseFloat(boundsInCurrency.min.amount),
                                max: parseFloat(boundsInCurrency.max.amount),
                            }"
                            :debounce="500"
                            :linear-percent="90"
                            :max-linear="5_000"
                            :steps="10_000"
                            :value-formatter="formatter"
                        />
                    </div>
                </div>

                <div
                    v-if="recommendations"
                    :class="classRecommendationsCards"
                    :data-qa-loading="loading"
                    class="recommendation-cards"
                >
                    <EngagementRingStoneRecommendationCard
                        v-for="(item, index) in recommendations"
                        :key="index"
                        :content="item.content"
                        :data="item.data"
                        :highlighted="item.highlighted"
                        @select="item.onSelect"
                    />
                </div>
            </div>

            <LoadingAnimation
                v-else-if="fetching || stale"
                class="loading-animation"
            />

            <div
                v-if="
                    !stoneRecommendations ||
                    stoneRecommendations?.__typename ===
                        'StoneRecommendationsResponseFailure'
                "
            >
                <div class="stone-recommendations-empty-state">
                    <ThSpacer size="LARGE" />

                    <ThTitle
                        alignment="CENTER"
                        color="var(--blue)"
                        size="SMALL"
                        tag="H3"
                    >
                        {{ t('stone_recommendations.ring_builder_empty') }}
                    </ThTitle>

                    <ThSpacer size="MEDIUM" />

                    <ThButton
                        :to="routes.engagementRings({})"
                        color="var(--blue)"
                        size="medium"
                        type="solid"
                    >
                        {{
                            t(
                                'stone_recommendations.ring_builder_empty_button_text'
                            )
                        }}
                    </ThButton>

                    <ThSpacer size="LARGE" />
                </div>
            </div>

            <PageErrorFiveHundred
                v-else-if="responseErrors.length"
                :errors="responseErrors"
            >
                <ThButton
                    :disabled="fetching"
                    @click="executeQuery"
                >
                    {{ t('globals.try_again') }}
                </ThButton>
            </PageErrorFiveHundred>

            <div
                v-if="stoneRecommendationsData?.cms?.fullSearchButtonText"
                class="all-results-cta"
            >
                <ThButton
                    v-qa="'choose-your-own'"
                    :to="expertSearchUrl"
                    color="var(--blue)"
                    type="bordered"
                    @click="trackOwnStoneSearch"
                >
                    {{ stoneRecommendationsData?.cms?.fullSearchButtonText }}
                </ThButton>
            </div>
        </div>

        <CmsBlocks
            v-if="blocks"
            :data="blocks"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, toRef } from 'vue'
import type { RouteLocationNamedRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { Bounds } from 'wucdbm-exponential-slider'

import { useRegionPlugin } from '@/plugins/region'
import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'
import { useRoutes } from '@/router/routes'
import { assertNever, includes } from '@/helpers'
import { useTranslator } from '@/i18n'
import {
    type EngagementRingStoneRecommendationCardFragment,
    // TH-15089 Toggle uncomment
    // type GemsApi_Stone,
    GemsApi_StoneCategory,
    GemsApi_StoneShape,
    type RingBuilderStoneRecommendationsFragment,
    StoneCategoryCode,
    type StoneRecommendationsFragment,
    type StoneRecommendationsNotFoundFragment,
    type StoneRecommendationsQueryVariables,
    type StoneShapeCode,
} from '@/graphql/api'
import { useStoneRecommendationsQuery } from '@/views/product/engagement-ring/stone-recommendations/StoneRecommendations.generated.ts'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'
import { useResponseError } from '@/helpers/errors'
import {
    type RecommendationsAnalyticsOption,
    useRecommendationsAnalytics,
} from '../../composables/analytics/useRecommendationsAnalytics'
import { useCurrencyInput } from '@/plugins/currency-input'
import { useMoneyFormatter } from '@/plugins/money-formatter'
import { useSpeakToExpertModal } from '@/composables/app/useSpeakToExpertModal'
import type { InputRangeTwoKnobbedValueFormatter } from '@/components/ui/inputs/range/input-range-two-knobbed-props'
import { useRingBuilder } from '@/plugins/ring-builder.ts'

import InputRangeTwoKnobbedExponentialOkWithInputs from '@/components/ui/inputs/range/InputRangeTwoKnobbedExponentialOkWithInputs.vue'
import LoadingAnimation from '@/components/ui/LoadingAnimation.vue'
import EngagementRingStoneRecommendationCard from '@/views/product/engagement-ring/stone-recommendations/EngagementRingStoneRecommendationCard.vue'
import CmsBlocks from '@/components/cms/CmsBlocks.vue'
import AvatarStack from '@/components/layout/avatar-stack/AvatarStack.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import ThSpacer from '@/components/ui/ThSpacer.vue'
import Tabs from '@/components/layout/Tabs.vue'
import LazyTooltip from '@/components/layout/tooltip/LazyTooltip.vue'

const props = defineProps<{
    category: GemsApi_StoneCategory
}>()

const { regionCode } = useRegionPlugin()
const { locale } = useTranslator()
const { t } = useI18n()

const routes = useRoutes()
const { ring, fetchingRingBuilderSetMutation, addStone } = useRingBuilder()

const { open: openSpeakToExpertModal } = useSpeakToExpertModal()

const ringGemsShapeCode = computed((): GemsApi_StoneShape | undefined => {
    const shape = ring.value?.centreStoneConstraints.shape.systemCode

    if (includes(Object.values(GemsApi_StoneShape), shape)) {
        return shape
    }

    return undefined
})

const ringStoneShapeCode = computed((): StoneShapeCode | undefined => {
    return ring.value?.centreStoneConstraints.shape.systemCode
})

const weight = computed(() => ({
    min: Number(ring.value?.centreStoneConstraints.weight.min) || null,
    max: Number(ring.value?.centreStoneConstraints.weight.max) || null,
}))

const expertSearchUrl = computed((): RouteLocationNamedRaw => {
    switch (props.category) {
        case GemsApi_StoneCategory.Diamond:
            return routes.diamondSearch(
                props.category,
                ringGemsShapeCode.value,
                {
                    weight: weight.value,
                }
            )
        case GemsApi_StoneCategory.LabGrownDiamond:
            return routes.nonSku.labGrownDiamondSearch({
                shape: ringStoneShapeCode.value,
                caratWeightRange: {
                    ...ring.value?.centreStoneConstraints.weight,
                },
            })
        case GemsApi_StoneCategory.FancyDiamond:
        // fallthrough
        case GemsApi_StoneCategory.LabGrownFancyDiamond:
            return routes.fancyDiamondSearch(
                props.category,
                undefined,
                ringGemsShapeCode.value,
                {
                    weight: weight.value,
                }
            )
        case GemsApi_StoneCategory.Gemstone:
            return routes.nonSku.gemstoneSearch({
                shape: ringStoneShapeCode.value,
                caratWeightRange: {
                    ...ring.value?.centreStoneConstraints.weight,
                },
            })
        default:
            return assertNever(props.category, {})
    }
})

const currency = useCurrencyInput()
const moneyFormatter = useMoneyFormatter()

const budgetFilter = ref<Bounds | null>(null)

onMounted(() => {
    budgetFilter.value = null
})

const budgetFilterForUi = computed<Bounds | null | undefined, Bounds | null>({
    get: () => {
        return budgetFilter.value ?? priceInitialValues.value
    },
    set: (value) => {
        budgetFilter.value = value
    },
})

const variables = computed((): StoneRecommendationsQueryVariables => {
    const bf = budgetFilter.value

    return {
        hasPrice: !!bf,
        minPrice: bf && {
            amount: bf.min.toString(),
            currency: currency.value,
        },
        maxPrice: bf && {
            amount: bf.max.toString(),
            currency: currency.value,
        },
        locale: locale.value,
        currency: currency.value,
        region: regionCode.value,
    }
})

const pause = ref(false)

const stoneRecommendationsResponse = useStoneRecommendationsQuery({
    variables,
    pause,
})

await stoneRecommendationsResponse

const {
    stale,
    fetching,
    data: stoneRecommendationsData,
    executeQuery,
} = stoneRecommendationsResponse

const responseErrors = useResponseError(stoneRecommendationsResponse)

const gemstoneCmsData = computed(() => {
    return Object.fromEntries(
        stoneRecommendationsData.value?.cms?.nonSkuGemStones.map((item) => {
            return [item.color, item.content]
        }) ?? []
    )
})

const getRecommendations = (
    input: RingBuilderStoneRecommendationsFragment | undefined | null
): StoneRecommendationsFragment | null => {
    if (input?.__typename === 'RingBuilderMountFragment') {
        return input.stoneRecommendations
    }

    return null
}

const stoneRecommendations = toRef(
    () =>
        getRecommendations(
            stoneRecommendationsData.value?.ringBuilder.position1
        ) ||
        getRecommendations(
            stoneRecommendationsData.value?.ringBuilder.position2
        )
)

const recommendations = computed<
    | {
          data: RingBuilderStoneTypes | StoneRecommendationsNotFoundFragment
          content: EngagementRingStoneRecommendationCardFragment | undefined
          highlighted?: boolean
          onSelect: (stone: RingBuilderStoneTypes) => void
      }[]
    | null
>(() => {
    if (!stoneRecommendations.value) {
        return null
    }

    if (!stoneRecommendationsData.value) {
        return null
    }

    if (
        stoneRecommendations.value?.__typename !==
        'StoneRecommendationsResponseSuccess'
    ) {
        return null
    }

    switch (props.category) {
        case GemsApi_StoneCategory.Diamond:
            return [
                {
                    content:
                        stoneRecommendationsData.value.cms?.naturalDiamonds
                            .forSizeCard,
                    data: stoneRecommendations.value.naturalDiamondBestForSize,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for size'),
                },
                {
                    content:
                        stoneRecommendationsData.value.cms?.naturalDiamonds
                            .forBalanceCard,
                    data: stoneRecommendations.value
                        .naturalDiamondBestForBalance,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for balance'),
                },
                {
                    content:
                        stoneRecommendationsData.value.cms?.naturalDiamonds
                            .labGrownAlternative,
                    data: stoneRecommendations.value.nonSkuLabGrownBestForPrice,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for price'),
                    highlighted: true,
                },
            ]

        case GemsApi_StoneCategory.LabGrownDiamond:
            return [
                {
                    content:
                        stoneRecommendationsData.value.cms?.nonSkuLabGrown
                            .forPriceCard,
                    data: stoneRecommendations.value.nonSkuLabGrownBestForPrice,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for price'),
                },
                {
                    content:
                        stoneRecommendationsData.value.cms?.nonSkuLabGrown
                            .forSizeCard,
                    data: stoneRecommendations.value.nonSkuLabGrownBestForSize,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for size'),
                },
                {
                    content:
                        stoneRecommendationsData.value.cms?.nonSkuLabGrown
                            .naturalDiamondAlternative,
                    data: stoneRecommendations.value.naturalDiamondBestForSize,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for balance'),
                    highlighted: true,
                },
            ]

        // TH-15089 Toggle delete lines -->
        case GemsApi_StoneCategory.Gemstone:
            return sortNonSkuStones(
                stoneRecommendations.value.nonSkuGemStones
            ).map((nonSkuGemstone) => ({
                content: gemstoneCmsData.value[nonSkuGemstone.color.systemCode],
                data: nonSkuGemstone.item,
                onSelect: (stone: RingBuilderStoneTypes) =>
                    selectStone(stone, 'Gemstone option'),
            }))
        // TH-15089 Toggle delete lines <---

        // TH-15089 Toggle uncomment
        // case GemsApi_StoneCategory.Gemstone:
        //     return sortAndTrimGemstones([
        //         ...stoneRecommendations.value.skuGemStones,
        //     ]).map((nonSkuGemstone) => ({
        //         content: gemstoneCmsData.value[nonSkuGemstone.color.systemCode],
        //         data: nonSkuGemstone.stone,
        //         onSelect: (stone: RingBuilderStoneTypes) =>
        //             selectStone(stone, 'Gemstone option'),
        //     }))
        default:
            return []
    }
})

const priceInitialValues = computed(() => {
    const v = stoneRecommendations.value
    if (
        v?.__typename === 'StoneRecommendationsResponseSuccess' &&
        v.priceInitialValues
    ) {
        return {
            min: parseFloat(v.priceInitialValues.min.amount),
            max: parseFloat(v.priceInitialValues.max.amount),
        }
    }
    return undefined
})

const tabs = [
    {
        name: GemsApi_StoneCategory.LabGrownDiamond,
        label: t('stone_recommendations.gemType.lab_grown'),
        labelExtra: t('stone_recommendations.gemCategory.diamonds'),
        to: routes.ringBuilder.recommendations('lab-grown-diamonds'),
        tooltipPath: 'diamond-search/tabs/lab-grown-diamonds',
    },
    {
        name: GemsApi_StoneCategory.Diamond,
        label: t('stone_recommendations.gemType.natural'),
        labelExtra: t('stone_recommendations.gemCategory.diamonds'),
        to: routes.ringBuilder.recommendations('natural-diamonds'),
        tooltipPath: 'diamond-search/tabs/natural-diamonds',
    },
    {
        name: StoneCategoryCode.Gemstone,
        label: t('stone_recommendations.gemCategory.gemstones'),
        to: routes.ringBuilder.recommendations('gemstones'),
        tooltipPath: 'diamond-search/tabs/gemstones',
    },
]

const currentTabResolver = (tab: Pick<(typeof tabs)[number], 'name'>) =>
    tab.name === props.category

const blocks = toRef(() => stoneRecommendationsData.value?.cms?.blocks)

const boundsInCurrency = computed(() => {
    const r = stoneRecommendations.value

    if (r?.__typename === 'StoneRecommendationsResponseSuccess') {
        return r.priceBounds
    }

    return undefined
})

const formatter: InputRangeTwoKnobbedValueFormatter = (value) =>
    moneyFormatter({
        amount: value.toString(),
        currency: boundsInCurrency.value?.min.currency || 'GBP',
    })

const { routerPush } = useRouterNavigation()

const { trackStoneSelected, trackOwnStoneSearch } = useRecommendationsAnalytics(
    toRef(() => props.category),
    budgetFilter
)

const selectStone = (
    stone: RingBuilderStoneTypes,
    option: RecommendationsAnalyticsOption
) => {
    const r = ring.value

    if (r) {
        addStone(stone).then(() => {
            trackStoneSelected(stone, option)

            routerPush(routes.ringBuilder.ring(r, stone))
        })
    } else {
        routerPush(routes.engagementRings({}))
    }
}

const loading = computed(
    () => fetching.value || stale.value || fetchingRingBuilderSetMutation.value
)

const classRecommendationsCards = computed(() => ({
    'fetching': loading.value,
}))
</script>

<script lang="ts">
// TH-15089 Toggle uncomment
// import type { StoneRecommendationGemsApiGemstoneFragment } from '@/graphql/api'
import {
    type StoneColorCode,
    // TH-15089 Toggle delete lines --->
    type StoneRecommendationNonSkuGemstoneItem,
    // TH-15089 Toggle delete lines <---
} from '@/graphql/api'

const colorPriorities: StoneColorCode[] = [
    'GEMSTONE_BLUE',
    'GEMSTONE_GREEN',
    'GEMSTONE_TEAL',
    'GEMSTONE_PEACH',
    'GEMSTONE_PINK',
    'GEMSTONE_PURPLE',
    'GEMSTONE_YELLOW',
]

// TH-15089 Toggle delete lines --->
const maxStones = 3

type SortType = {
    color: { systemCode: StoneColorCode }
    item: {
        __typename: StoneRecommendationNonSkuGemstoneItem['__typename']
    }
}
const sortNonSkuStones = <S extends SortType>(stones: S[]): S[] => {
    const stonesIndexed = Object.fromEntries(
        stones.map((item) => {
            return [item.color.systemCode, item]
        })
    )

    const sortedStones: S[] = []

    const colorsWithoutStones: StoneColorCode[] = []
    let prioIndex = 0
    do {
        const color = colorPriorities[prioIndex++]!
        const stoneByColor = stonesIndexed[color]
        if (stoneByColor?.item['__typename'] === 'NonSkuGemstone') {
            sortedStones.push(stoneByColor)
        } else {
            colorsWithoutStones.push(color)
        }
    } while (
        sortedStones.length < maxStones &&
        prioIndex < colorPriorities.length
    )

    const sortedStonesLength = sortedStones.length
    if (sortedStonesLength < maxStones) {
        for (let i = 0; i < maxStones - sortedStonesLength; i++) {
            sortedStones.push(stonesIndexed[colorsWithoutStones[i]!]!)
        }
    }

    return sortedStones
}

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    const foundStone = {
        __typename: 'NonSkuGemstone',
    } as const

    const notFoundStone = {
        __typename: 'StoneRecommendationNotFound',
    } as const

    const makeStone = (
        color: StoneColorCode,
        foundOrNotFound: SortType['item']
    ): SortType => {
        return {
            color: {
                systemCode: color,
            },
            item: foundOrNotFound,
        }
    }

    describe('Stone Recommendations', () => {
        test('Sorted Gemstones, first prio not found', () => {
            const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
            const green = makeStone('GEMSTONE_GREEN', notFoundStone)
            const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
            const peach = makeStone('GEMSTONE_PEACH', foundStone)
            const pink = makeStone('GEMSTONE_PINK', notFoundStone)
            const purple = makeStone('GEMSTONE_PURPLE', foundStone)
            const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

            const sorted = sortNonSkuStones([
                blue,
                green,
                teal,
                peach,
                pink,
                purple,
                yellow,
            ])

            expect(sorted).length(3)
            expect(sorted[0]).toBe(peach)
            expect(sorted[1]).toBe(purple)
            expect(sorted[2]).toBe(blue)
        })

        test('Sorted Gemstones, one found', () => {
            const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
            const green = makeStone('GEMSTONE_GREEN', notFoundStone)
            const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
            const peach = makeStone('GEMSTONE_PEACH', foundStone)
            const pink = makeStone('GEMSTONE_PINK', notFoundStone)
            const purple = makeStone('GEMSTONE_PURPLE', notFoundStone)
            const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

            const sorted = sortNonSkuStones([
                blue,
                green,
                teal,
                peach,
                pink,
                purple,
                yellow,
            ])

            expect(sorted).length(3)
            expect(sorted[0]).toBe(peach)
            expect(sorted[1]).toBe(blue)
            expect(sorted[2]).toBe(green)
        })

        test('Sorted Gemstones, nothing found', () => {
            const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
            const green = makeStone('GEMSTONE_GREEN', notFoundStone)
            const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
            const peach = makeStone('GEMSTONE_PEACH', notFoundStone)
            const pink = makeStone('GEMSTONE_PINK', notFoundStone)
            const purple = makeStone('GEMSTONE_PURPLE', notFoundStone)
            const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

            const sorted = sortNonSkuStones([
                blue,
                green,
                teal,
                peach,
                pink,
                purple,
                yellow,
            ])

            expect(sorted).length(3)
            expect(sorted[0]).toBe(blue)
            expect(sorted[1]).toBe(green)
            expect(sorted[2]).toBe(teal)
        })

        test('Sorted Gemstones, last 3 found', () => {
            const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
            const green = makeStone('GEMSTONE_GREEN', notFoundStone)
            const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
            const peach = makeStone('GEMSTONE_PEACH', notFoundStone)
            const pink = makeStone('GEMSTONE_PINK', foundStone)
            const purple = makeStone('GEMSTONE_PURPLE', foundStone)
            const yellow = makeStone('GEMSTONE_YELLOW', foundStone)

            const sorted = sortNonSkuStones([
                blue,
                green,
                teal,
                peach,
                pink,
                purple,
                yellow,
            ])

            expect(sorted).length(3)
            expect(sorted[0]).toBe(pink)
            expect(sorted[1]).toBe(purple)
            expect(sorted[2]).toBe(yellow)
        })
    })
}
// TH-15089 Toggle delete lines <---

// TH-15089 Toggle uncomment
// const sortAndTrimGemstones = <
//     TStones extends {
//         color: {
//             systemCode: StoneRecommendationGemsApiGemstoneFragment['color']['systemCode']
//         }
//         stone: {
//             __typename: StoneRecommendationGemsApiGemstoneFragment['stone']['__typename']
//         }
//     },
// >(
//     gemstones: TStones[]
// ): TStones[] =>
//     gemstones
//         .sort((a, b) => {
//             let aIndex = colorPriorities.indexOf(a.color.systemCode)
//             let bIndex = colorPriorities.indexOf(b.color.systemCode)

//             if (aIndex === -1) {
//                 aIndex = 999
//             }

//             if (bIndex === -1) {
//                 bIndex = 999
//             }

//             if (a.stone.__typename == 'StoneRecommendationNotFound') {
//                 aIndex = 999
//             }

//             if (b.stone.__typename == 'StoneRecommendationNotFound') {
//                 bIndex = 999
//             }

//             return aIndex - bIndex
//         })
//         .slice(0, 3)

// if (import.meta.vitest) {
//     const { describe, test, expect } = import.meta.vitest

//     type SortType = {
//         color: { systemCode: StoneColorCode }
//         stone: {
//             __typename: StoneRecommendationGemsApiGemstoneFragment['stone']['__typename']
//         }
//     }

//     const foundStone = {
//         __typename: 'GemsApi_Stone',
//     } as const

//     const notFoundStone = {
//         __typename: 'StoneRecommendationNotFound',
//     } as const

//     const makeStone = (
//         color: StoneColorCode,
//         foundOrNotFound: SortType['stone']
//     ): SortType => {
//         return {
//             color: {
//                 systemCode: color,
//             },
//             stone: foundOrNotFound,
//         }
//     }

//     describe('Stone Recommendations', () => {
//         test('Sorted Gemstones, first prio not found', () => {
//             const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
//             const green = makeStone('GEMSTONE_GREEN', notFoundStone)
//             const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
//             const peach = makeStone('GEMSTONE_PEACH', foundStone)
//             const pink = makeStone('GEMSTONE_PINK', notFoundStone)
//             const purple = makeStone('GEMSTONE_PURPLE', foundStone)
//             const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

//             const sorted = sortAndTrimGemstones([
//                 blue,
//                 green,
//                 teal,
//                 peach,
//                 pink,
//                 purple,
//                 yellow,
//             ])

//             expect(sorted).length(3)
//             expect(sorted[0]).toBe(peach)
//             expect(sorted[1]).toBe(purple)
//             expect(sorted[2]).toBe(blue)
//         })

//         test('Sorted Gemstones, one found', () => {
//             const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
//             const green = makeStone('GEMSTONE_GREEN', notFoundStone)
//             const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
//             const peach = makeStone('GEMSTONE_PEACH', foundStone)
//             const pink = makeStone('GEMSTONE_PINK', notFoundStone)
//             const purple = makeStone('GEMSTONE_PURPLE', notFoundStone)
//             const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

//             const sorted = sortAndTrimGemstones([
//                 blue,
//                 green,
//                 teal,
//                 peach,
//                 pink,
//                 purple,
//                 yellow,
//             ])

//             expect(sorted).length(3)
//             expect(sorted[0]).toBe(peach)
//             expect(sorted[1]).toBe(blue)
//             expect(sorted[2]).toBe(green)
//         })

//         test('Sorted Gemstones, nothing found', () => {
//             const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
//             const green = makeStone('GEMSTONE_GREEN', notFoundStone)
//             const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
//             const peach = makeStone('GEMSTONE_PEACH', notFoundStone)
//             const pink = makeStone('GEMSTONE_PINK', notFoundStone)
//             const purple = makeStone('GEMSTONE_PURPLE', notFoundStone)
//             const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

//             const sorted = sortAndTrimGemstones([
//                 blue,
//                 green,
//                 teal,
//                 peach,
//                 pink,
//                 purple,
//                 yellow,
//             ])

//             expect(sorted).length(3)
//             expect(sorted[0]).toBe(blue)
//             expect(sorted[1]).toBe(green)
//             expect(sorted[2]).toBe(teal)
//         })

//         test('Sorted Gemstones, last 3 found', () => {
//             const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
//             const green = makeStone('GEMSTONE_GREEN', notFoundStone)
//             const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
//             const peach = makeStone('GEMSTONE_PEACH', notFoundStone)
//             const pink = makeStone('GEMSTONE_PINK', foundStone)
//             const purple = makeStone('GEMSTONE_PURPLE', foundStone)
//             const yellow = makeStone('GEMSTONE_YELLOW', foundStone)

//             const sorted = sortAndTrimGemstones([
//                 blue,
//                 green,
//                 teal,
//                 peach,
//                 pink,
//                 purple,
//                 yellow,
//             ])

//             expect(sorted).length(3)
//             expect(sorted[0]).toBe(pink)
//             expect(sorted[1]).toBe(purple)
//             expect(sorted[2]).toBe(yellow)
//         })
//     })
// }
</script>

<style lang="scss" scoped>
.loading-animation {
    margin: 300px 0;
}

.consultant-message {
    width: 440px;
    max-width: 100%;
    padding: 0 20px;
    margin: 40px auto;
    text-align: center;
    line-height: 1.5em;

    .instruction-text {
        margin: 20px 0;

        .cta {
            cursor: pointer;
            text-decoration: underline;
        }
    }
}

.stone-recommendations-empty-state {
    width: 100%;
    max-width: 800px;
    margin: 10vh auto;
    text-align: center;
}

.recommendations-wrapper {
    width: 1140px;
    max-width: 100%;
    margin: 0 auto;
    position: relative;

    .budget-slider {
        width: 840px;
        margin: 0 auto;
        max-width: 100%;
        padding: 20px 20px 20px;

        @include min-width(768) {
            display: flex;
            align-items: center;
        }

        .label {
            display: flex;
            align-content: center;
            margin: 0 40px 0 0;
            white-space: nowrap;

            .icon {
                margin-left: 5px;
                font-size: 0.875rem;
            }
        }

        .range-slider {
            width: 100%;
        }
    }
}

.recommendation-cards {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: center;
    padding: 0 20px;

    @include fetching;

    @include min-width(640) {
        flex-wrap: nowrap;
    }
}

.all-results-cta {
    width: 100%;
    display: flex;
    justify-content: center;
    margin: 60px 0 80px;
}
</style>
